
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface AuthContextType {
    token: string | null;
    setToken: (newToken?: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // State to hold the authentication token
    const [token, setToken_] = useState<string | null>(localStorage.getItem("token"));

    // Function to set the authentication token
    const setToken = (newToken?: string | null) => {
        if (newToken === undefined) {
            setToken_(null);
        } else {
            setToken_(newToken);
        }
    };

    useEffect(() => {
        if (token) {
            let decodedToken: any = jwtDecode(token);
            let currentDate = new Date();
            // JWT exp is in seconds
            if (decodedToken.exp && decodedToken.exp * 1000 < currentDate.getTime()) {
                // Token expired. Remove and clean it. This will force a redirect to the login page.
                localStorage.removeItem("token");
                setToken();
            } else {
                // Token valid
                localStorage.setItem("token", token);
            }
        } else {
            // Token is not set.
            localStorage.removeItem("token");
        }
    }, [token]);

    // Memoized value of the authentication context
    const contextValue = useMemo(
        () => ({
            token,
            setToken,
        }),
        [token]
    );

    // Provide the authentication context to the children components
    return (
        <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthProvider;
